import { FC } from 'react';
import {
  Box,
  FormControlLabel,
  Switch,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import ListTable from '../../components/ListTable';
import { ListCard, ListCards } from '../../components/ListCards';
import { colors, fontWeights } from '../../theme/tokens';
import EntityRowActions, { ENTITY_ACTIONS_WIDTH } from './EntityRowActions';
import { EntityAction, EntityRow } from './types';

// The services / functions table with the pattern of the users list:
// `Nom` · `Actif` (switch) · actions. On mobile, cards.
//
// A removed entity disappears from the listing, so each row is active
// or deactivated, never deleted. The deactivated ones are dimmed (except their
// actions, which remain the path to reactivate them).

export const COLUMNS = ['Nom', 'Actif'] as const;

export const ENTITIES_ERROR_TEXT = 'Impossible de charger la liste.';
export const ENTITIES_EMPTY_TEXT = 'Aucun élément pour le moment.';

type Props = {
  entities: EntityRow[];
  isLoading: boolean;
  isError: boolean;
  /** Row whose switch is changing, to block it in the meantime. */
  togglingId?: string;
  onToggle: (entity: EntityRow) => void;
  onAction: (action: EntityAction, entity: EntityRow) => void;
  onRetry: () => void;
};

const INACTIVE_SX = { opacity: 0.55 } as const;

const ActiveSwitch: FC<{
  entity: EntityRow;
  disabled: boolean;
  onToggle: (entity: EntityRow) => void;
}> = ({ entity, disabled, onToggle }) => (
  <Switch
    color="secondary"
    checked={entity.active}
    disabled={disabled}
    onChange={() => onToggle(entity)}
    inputProps={{
      'aria-label': `Activer ou désactiver ${entity.name}`,
    }}
  />
);

export const EntitiesTable: FC<Props> = ({
  entities,
  isLoading,
  isError,
  togglingId,
  onToggle,
  onAction,
  onRetry,
}) => (
  <ListTable
    ariaLabel="Liste"
    columns={COLUMNS}
    actionsWidth={ENTITY_ACTIONS_WIDTH}
    minWidth={520}
    isLoading={isLoading}
    isError={isError}
    isEmpty={entities.length === 0}
    errorText={ENTITIES_ERROR_TEXT}
    emptyText={ENTITIES_EMPTY_TEXT}
    onRetry={onRetry}
  >
    {entities.map((entity) => (
      <TableRow key={entity.id} data-active={entity.active ? 'true' : 'false'}>
        <TableCell sx={entity.active ? undefined : INACTIVE_SX}>
          <Typography
            component="span"
            sx={{ fontWeight: fontWeights.medium, overflowWrap: 'anywhere' }}
          >
            {entity.name}
          </Typography>
        </TableCell>
        <TableCell sx={{ width: 120 }}>
          <ActiveSwitch
            entity={entity}
            disabled={togglingId === entity.id}
            onToggle={onToggle}
          />
        </TableCell>
        <TableCell align="right">
          <EntityRowActions entity={entity} variant="table" onAction={onAction} />
        </TableCell>
      </TableRow>
    ))}
  </ListTable>
);

export const EntityCards: FC<Props> = ({
  entities,
  isLoading,
  isError,
  togglingId,
  onToggle,
  onAction,
  onRetry,
}) => (
  <ListCards
    ariaLabel="Liste"
    isLoading={isLoading}
    isError={isError}
    isEmpty={entities.length === 0}
    errorText={ENTITIES_ERROR_TEXT}
    emptyText={ENTITIES_EMPTY_TEXT}
    onRetry={onRetry}
  >
    {entities.map((entity) => (
      <ListCard key={entity.id}>
        <Typography
          component="span"
          sx={{
            fontWeight: fontWeights.medium,
            overflowWrap: 'anywhere',
            ...(entity.active ? {} : INACTIVE_SX),
          }}
        >
          {entity.name}
        </Typography>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <FormControlLabel
            control={
              <ActiveSwitch
                entity={entity}
                disabled={togglingId === entity.id}
                onToggle={onToggle}
              />
            }
            label={entity.active ? 'Actif' : 'Inactif'}
            sx={{
              m: 0,
              '& .MuiFormControlLabel-label': {
                fontSize: '0.875rem',
                color: colors.textBase,
              },
            }}
          />
          <EntityRowActions entity={entity} variant="card" onAction={onAction} />
        </Box>
      </ListCard>
    ))}
  </ListCards>
);
